import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import {
  Loader2, MessagesSquare, RotateCcw, Bot, UserCheck, MoonStar, PhoneOff, UserPlus, ListChecks, CalendarCheck, Timer,
} from "lucide-react";

const NAVY = "#1C4358", RED = "#9C1E22", GREEN = "#2F7D5A", AMBER = "#B7791F", SKY = "#2C6480", PURPLE = "#7C3AED";
const COLORS = [NAVY, RED, GREEN, AMBER, SKY, PURPLE, "#2563EB", "#C2620F", "#5A5E2E", "#9AA5B1"];

const Kpi = ({ icon: Icon, label, value, color, testid }) => (
  <div className="rounded-xl border border-[#E6EDF2] bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2"><Icon size={16} style={{ color }} /><div className="text-[11px] text-[#4B5563]">{label}</div></div>
    <div className="text-2xl font-display font-bold mt-1 tabular-nums" style={{ color }}>{value}</div>
  </div>
);

const List = ({ items }) => (!items || items.length === 0) ? <p className="text-[13px] text-[#9AA5B1] py-2">No data yet.</p> : (
  <ul className="space-y-1.5">{items.map((it, i) => (
    <li key={i} className="flex items-center justify-between gap-2 text-[13px]"><span className="text-[#252221] truncate">{it.name}</span><span className="tabular-nums font-semibold text-[#1C4358]">{it.value}</span></li>
  ))}</ul>
);

const Bars = ({ items, color = NAVY }) => (!items || items.length === 0) ? <p className="text-[13px] text-[#9AA5B1] py-2">No data yet.</p> : (
  <div style={{ width: "100%", height: 210 }}><ResponsiveContainer>
    <BarChart data={items} margin={{ top: 6, right: 8, left: -18, bottom: 4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F6" vertical={false} />
      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#4B5563" }} interval={0} angle={-18} textAnchor="end" height={54} />
      <YAxis tick={{ fontSize: 10, fill: "#4B5563" }} allowDecimals={false} />
      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E6EDF2" }} />
      <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
    </BarChart></ResponsiveContainer></div>
);

const Block = ({ title, children, testid }) => (
  <Card className="p-4 border-[#E6EDF2]" data-testid={testid}>
    <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">{title}</div>{children}
  </Card>
);

export default function ChatAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/chat/analytics", { params: { days: Number(days) } }); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load analytics."); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  if (!data) return null;
  const conv = [
    { name: "Leads", value: data.converted_to_leads },
    { name: "Follow-up tasks", value: data.converted_to_tasks },
    { name: "Consultations", value: data.converted_to_consultations },
  ];

  return (
    <div className="pb-16" data-testid="chat-analytics">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2"><MessagesSquare size={22} className="text-[#1C4358]" /><h1 className="text-2xl font-display font-bold text-[#1C4358]">Chat Analytics</h1></div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}><SelectTrigger data-testid="analytics-range" className="w-[130px] h-9 text-[13px]"><SelectValue /></SelectTrigger>
            <SelectContent>{["7", "30", "90", "365"].map((d) => <SelectItem key={d} value={d}>Last {d} days</SelectItem>)}</SelectContent></Select>
          <Button variant="outline" size="sm" onClick={load} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi icon={MessagesSquare} label="Total chats" value={data.total} color={NAVY} testid="analytics-total" />
        <Kpi icon={Bot} label="AI-only" value={data.ai_only} color={SKY} testid="analytics-ai-only" />
        <Kpi icon={UserCheck} label="Human handoff" value={data.human_handoff} color={GREEN} testid="analytics-handoff" />
        <Kpi icon={MoonStar} label="Offline" value={data.offline} color={AMBER} testid="analytics-offline" />
        <Kpi icon={PhoneOff} label="Missed" value={data.missed} color={RED} testid="analytics-missed" />
        <Kpi icon={Timer} label="Avg response" value={data.avg_response_minutes != null ? `${data.avg_response_minutes}m` : "—"} color={PURPLE} testid="analytics-avg-response" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Kpi icon={UserPlus} label="Converted to leads" value={data.converted_to_leads} color={NAVY} testid="analytics-conv-leads" />
        <Kpi icon={ListChecks} label="Converted to follow-up tasks" value={data.converted_to_tasks} color={GREEN} testid="analytics-conv-tasks" />
        <Kpi icon={CalendarCheck} label="Converted to consultations" value={data.converted_to_consultations} color={SKY} testid="analytics-conv-consults" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Block title="Chats by service lane" testid="analytics-by-lane"><Bars items={data.by_service_lane} color={NAVY} /></Block>
        <Block title="Conversions" testid="analytics-conversions">
          <div style={{ width: "100%", height: 210 }}><ResponsiveContainer>
            <PieChart><Pie data={conv} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={72} label={{ fontSize: 10 }}>
              {conv.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E6EDF2" }} /></PieChart>
          </ResponsiveContainer></div>
        </Block>
        <Block title="Chats by page path" testid="analytics-by-page"><List items={data.by_page_path} /></Block>
        <Block title="Chats by reverse-search topic" testid="analytics-by-reverse"><List items={data.by_reverse_topic} /></Block>
        <Block title="Chats by territory / franchise" testid="analytics-by-territory"><List items={data.by_territory} /></Block>
      </div>
    </div>
  );
}
